// Every invoice: the drafts being laid out, and the documents that went out.
//
// One list rather than two, filtered by status, because the two questions it
// answers are read in the same sitting — "what am I about to send" and "what did
// I send in September" — and a screen that split them would make comparing a
// draft against last month's an act of navigation.
//
// A draft that still carries an unpriced line is called out here rather than
// only on the document, because that is the state somebody needs to see WITHOUT
// opening anything: it is the difference between "these three are ready" and
// "these three, but one is still waiting on Hansen".
//
// AND EVERY ROW SAYS WHICH HALF OF THE SYSTEM ITS MONEY IS. A charge for
// something we sell and a cost of ours coming back are not the same money, and
// this list reported them as one figure with nothing to tell them apart: an
// expense recovery and a subscription bill for the same customer in the same
// month were two rows differing only in the number on them. The "For" column is
// that fact — which half, then what kind of charge inside it — and it sits
// beside the customer because the two together are what identify a document.
// See `components/ChargeMix.tsx` for the vocabulary and why it is shared.
//
// EVERY ROW LEADS WITH THE CUSTOMER. The partner is who pays, and on a list of
// documents that is the half they have in common — five Hansen invoices were
// five identical-looking rows, and "5 Hansen bills to send" is not a sentence
// anybody can act on. What distinguishes one row from the next is whose service
// it bills, so `customers` is the strong line and the payer rides underneath.
// The document itself still addresses the partner; see `InvoiceSheet`.

import { useCallback, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { InvoiceStatus, type InvoiceListItem } from "@crm/shared";
import styles from "./InvoicesPage.module.css";
import { summariseCustomers } from "./customers.js";
import { NewInvoiceDialog } from "./NewInvoiceDialog.js";
import { ChargeMixBadge } from "../../components/ChargeMix.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card } from "../../design/Card.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, isRowBackgroundClick, Table, tableStyles, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useInvoices, type InvoiceQueryState } from "../../api/queries.js";
import { formatCalendarDate, formatMonth } from "../../format/calendarDate.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import { chargeTypeLabel } from "../../format/labels.js";
import { useHasRole } from "../../auth/useAuth.js";

const STATUS_TONE: Record<InvoiceListItem["status"], Tone> = {
  draft: "neutral",
  issued: "info",
  paid: "good",
  void: "bad",
};

const STATUS_FILTERS = [
  { value: "", label: "All" },
  ...InvoiceStatus.options.map((status) => ({ value: status, label: status })),
];

const PAGE_SIZE = 25;

export function InvoicesPage(): ReactNode {
  const canEdit = useHasRole("crm_editor");
  const navigate = useNavigate();
  const [status, setStatus] = useState<InvoiceStatus | "">("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const query = useMemo<InvoiceQueryState>(
    () => ({
      statuses: status === "" ? [] : [status],
      partnerId: null,
      page,
      pageSize: PAGE_SIZE,
    }),
    [status, page],
  );
  const invoices = useInvoices(query);

  usePageChrome({
    title: "Invoices",
  });

  // A new draft opens straight onto its own sheet: creating one is never the
  // goal, laying it out is.
  const onCreated = useCallback(
    (invoice: InvoiceListItem) => {
      setCreating(false);
      void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
    },
    [navigate],
  );

  const total = invoices.data?.total ?? 0;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <label className={styles.filter}>
          <span className={styles.filterLabel}>Status:</span>
          <select
            className={styles.select}
            value={status}
            onChange={(event) => {
              const parsed = InvoiceStatus.safeParse(event.target.value);
              setStatus(parsed.success ? parsed.data : "");
              setPage(1);
            }}
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {canEdit && (
          <Button variant="primary" small onClick={() => setCreating(true)}>
            New invoice
          </Button>
        )}
      </div>

      <Card>
        <QueryBoundary query={invoices} label="the invoices">
          {(envelope) => (
            <Table label="Invoices">
              <thead>
                <tr>
                  <Th>Number</Th>
                  {/* One column, two facts, named for the one it leads with —
                      a "Payer" column and a "Customer" column side by side
                      would push the four numeric columns off a laptop, and the
                      payer is a qualifier on the customer here rather than
                      something anybody scans down. */}
                  <Th>Customer</Th>
                  {/* Which half, and what kind of charge within it. Named "For"
                      rather than "Kind" because the two lines in the cell answer
                      one question between them: what is this document for. */}
                  <Th>For</Th>
                  <Th>Covers</Th>
                  <Th>Due</Th>
                  <Th numeric>Lines</Th>
                  {/* What the end customers were charged, then what this
                      document asks for, then what reaches us — the pair the
                      billing screen shows everywhere else, at document scale.
                      "Total" alone is the net (0024) and cannot be compared
                      against anything without the base beside it. */}
                  <Th numeric>Customer billed</Th>
                  <Th numeric>Total</Th>
                  <Th numeric>Ours</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {envelope.items.length === 0 ? (
                  <EmptyRow
                    colSpan={10}
                    title="No invoices"
                    description="Start one from a bill run on the billing screen, or with “New invoice” above."
                  />
                ) : (
                  envelope.items.map((invoice) => <Row key={invoice.id} invoice={invoice} />)
                )}
              </tbody>
            </Table>
          )}
        </QueryBoundary>
      </Card>

      {total > PAGE_SIZE && (
        <div className={styles.paging}>
          <span>{`${String((page - 1) * PAGE_SIZE + 1)}–${String(Math.min(page * PAGE_SIZE, total))} of ${String(total)}`}</span>
          <span className={styles.spacer} />
          <Button small disabled={page === 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Button small disabled={page * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}

      {creating && <NewInvoiceDialog onClose={() => setCreating(false)} onCreated={onCreated} />}
    </div>
  );
}

function Row({ invoice }: { invoice: InvoiceListItem }): ReactNode {
  const navigate = useNavigate();
  const customers = summariseCustomers(invoice.customers);

  // THE WHOLE ROW OPENS THE DOCUMENT, not just the eight characters of its
  // number. This row has carried `rowLink` — pointer cursor, hover highlight —
  // since it was written, which is the app's way of saying "this navigates";
  // for a while it said that and did nothing, and a reader who aimed at the
  // customer name (the thing the row is READ by, see the note at the top) got
  // no invoice. Every other list that claims a row does the same thing:
  // partners, deals, the billing schedule.
  //
  // `isRowBackgroundClick` is what makes the claim safe to keep: a click that
  // landed on the number's own link — or on any control a later column adds —
  // keeps its own action and never also fires this one, so the invoice is
  // opened exactly once however it was reached.
  const open = useCallback(
    (event: MouseEvent<HTMLTableRowElement>): void => {
      if (!isRowBackgroundClick(event)) return;
      void navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
    },
    [invoice.id, navigate],
  );

  return (
    <tr className={tableStyles.rowLink} onClick={open}>
      <Td strong>
        {/* A real link inside the clickable row: a `<tr>` is not reachable by
            keyboard and never appears in a screen reader's list of links, so
            this anchor is the only way in that is not a mouse — and it is what
            makes the row middle-clickable and ⌘-clickable into a new tab. */}
        <Link to="/invoices/$invoiceId" params={{ invoiceId: invoice.id }}>
          {invoice.invoice_number ?? "Draft"}
        </Link>
      </Td>
      {/* Two names too many is the common case on a bill run, so the cell shows
          what fits and COUNTS the rest — `+2` answers "how big is this one"
          where an ellipsis only says "there is more". The full list is in the
          tooltip, and only when something was actually cut.

          An invoice naming no customer is not an empty cell: nothing on it
          points at a deal, which happens to a partner-wide recharge or a
          document of standalone lines. It says so, with the payer still under
          it, so the row is never mistaken for one that failed to load. */}
      <Td strong>
        <div {...(customers.full === null ? {} : { title: customers.full })}>
          {customers.isPartnerWide ? (
            <span className={styles.partnerWide}>Partner-wide</span>
          ) : (
            customers.label
          )}
        </div>
        <div className={styles.payer}>{invoice.partner_name}</div>
      </Td>
      {/* WHICH HALF, THEN WHAT KIND. The badge is the distinction the reader
          came for — is this money we earned or money we are getting back — and
          the charge types under it are what the document is about inside that.
          Plain text rather than a row of tags for the same reason the bill-run
          card gives: a document of four kinds would be four badges in a narrow
          cell, and the badge above already carries the colour.

          A draft with nothing on it yet renders neither, which is honest: it is
          not yet a document of any kind. */}
      <Td>
        <ChargeMixBadge
          counts={{
            bills: invoice.bill_line_count,
            recharges: invoice.recharge_line_count,
            standalone: invoice.standalone_line_count,
          }}
        />
        {invoice.charge_types.length > 0 && (
          <div className={styles.chargeTypes}>
            {invoice.charge_types.map(chargeTypeLabel).join(" · ")}
          </div>
        )}
      </Td>
      <Td muted mono nowrap>
        {invoice.service_period === null ? "—" : formatMonth(invoice.service_period)}
      </Td>
      <Td mono nowrap>
        {formatCalendarDate(invoice.due_date)}
      </Td>
      <Td numeric mono muted>
        {formatCount(invoice.line_count)}
      </Td>
      <Td numeric mono strong>
        {formatMoney(invoice.charged_minor, invoice.currency, { omitCurrencyCode: true })}
        <div className={styles.currency}>{invoice.currency}</div>
      </Td>
      <Td numeric mono muted>
        {formatMoney(invoice.our_share_minor, invoice.currency, { omitCurrencyCode: true })}
        {/* The blended rate, under our share, exactly where `SplitMark` puts it
            on the billing table — so the two screens read the same way. Absent
            rather than 0% on a document with no revenue on it. */}
        {invoice.effective_split_pct !== null && (
          <span className={styles.blendedRate}>{formatPercent(invoice.effective_split_pct)}</span>
        )}
      </Td>
      <Td>
        <div className={styles.statusCell}>
          <Badge tone={STATUS_TONE[invoice.status]} shape="chip">
            {invoice.status}
          </Badge>
          {/* The state somebody needs to see without opening the document. */}
          {invoice.pending_line_count > 0 && (
            <Badge tone="warn" shape="tag">
              {`${formatCount(invoice.pending_line_count)} pending`}
            </Badge>
          )}
        </div>
      </Td>
    </tr>
  );
}
